if (!customElements.get('branch-availability')) {
  customElements.define(
    'branch-availability',
    class BranchAvailability extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.isInline = this.hasAttribute('data-inline');
        this.button = this.querySelector('.branch-availability__button');
        this.spinner = this.querySelector('.loading-overlay__spinner');
        this.modal = this.dataset.modal ? document.querySelector(this.dataset.modal) : null;
        this.text = {
          available: this.dataset.textAvailable,
          notAvailable: this.dataset.textNotAvailable,
          error: this.dataset.textError,
          empty: this.dataset.textEmpty,
          loading: this.dataset.textLoading,
          none: this.dataset.textNone || 'המידה הזו לא זמינה כרגע בחנויות',
        };
        this.loadedSku = null;
        this.request = null;

        if (this.isInline) {
          this.details = this.closest('details');
          if (this.details) {
            this.details.addEventListener('toggle', () => {
              if (this.details.open) this.load();
            });
            if (this.details.open) this.load();
          }
          this.onVariantChange = this.onVariantChange.bind(this);
          document.addEventListener('change', this.onVariantChange);
        } else if (this.button) {
          this.button.addEventListener('click', this.onClick.bind(this));
        }
      }

      // Root used to look up the icon templates (inside the element in inline mode, inside the modal otherwise)
      get root() {
        return this.isInline ? this : this.modal;
      }

      get body() {
        return this.root && this.root.querySelector('[data-branch-availability-body]');
      }

      // ---------- Inline (accordion) mode ----------
      load() {
        const sku = this.dataset.sku;
        if (!this.dataset.endpoint || !sku || this.loadedSku === sku || this.request === sku) return;
        this.request = sku;
        this.renderMessage(this.text.loading || '...', true);
        this.fetchBranches(sku)
          .then((data) => {
            if (this.dataset.sku !== sku) return; // size changed while loading
            this.loadedSku = sku;
            if (!this.hasBranches(data)) return this.renderMessage(this.text.empty);
            // Inline mode: show only branches that have the selected size in stock
            const inStock = data.branches.filter((branch) => Number(branch.inventory_status) > 0);
            inStock.length ? this.renderBranches(inStock) : this.renderMessage(this.text.none);
          })
          .catch((error) => {
            console.error('BranchAvailability:', error);
            if (this.dataset.sku === sku) this.renderMessage(this.text.error);
          })
          .finally(() => {
            if (this.request === sku) this.request = null;
            if (this.dataset.sku !== sku && this.details && this.details.open) this.load();
          });
      }

      onVariantChange(event) {
        const input = event.target;
        if (!this.isConnected || !input || input.name !== 'id') return;
        const form = input.closest('form');
        if (!form || !this.closest('section') || !this.closest('section').contains(form)) return;
        const variant = this.findVariant(input.value);
        if (!variant || !variant.sku || variant.sku === this.dataset.sku) return;
        this.dataset.sku = variant.sku;
        this.loadedSku = null;
        if (this.details && this.details.open) this.load();
        else if (this.body) this.body.replaceChildren();
      }

      findVariant(id) {
        const section = this.closest('section');
        const json = section && section.querySelector('variant-radios script[type="application/json"], variant-selects script[type="application/json"]');
        if (!json) return null;
        try {
          return JSON.parse(json.textContent).find((variant) => String(variant.id) === String(id)) || null;
        } catch (e) {
          return null;
        }
      }

      // ---------- Modal (button) mode ----------
      showLoader() {
        this.button.setAttribute('aria-busy', 'true');
        this.button.classList.add('loading');
        this.spinner && this.spinner.classList.remove('hidden');
      }

      hideLoader() {
        this.button.removeAttribute('aria-busy');
        this.button.classList.remove('loading');
        this.spinner && this.spinner.classList.add('hidden');
      }

      onClick() {
        const sku = this.dataset.sku;
        if (!this.dataset.endpoint || !sku || !this.modal) return;
        this.showLoader();
        this.fetchBranches(sku)
          .then((data) => {
            this.hasBranches(data) ? this.renderBranches(data.branches) : this.renderMessage(this.text.empty);
            this.openModal();
          })
          .catch((error) => {
            console.error('BranchAvailability:', error);
            this.renderMessage(this.text.error);
            this.openModal();
          })
          .finally(() => this.hideLoader());
      }

      openModal() {
        this.modal && this.modal.show(this.button);
      }

      // ---------- Shared ----------
      fetchBranches(sku) {
        return fetch(this.dataset.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-shopify-shop-domain': this.dataset.shopDomain },
          body: JSON.stringify({ sku }),
        }).then((response) => {
          if (!response.ok) throw new Error(`Network response failed: ${response.status} ${response.statusText}`);
          return response.json();
        });
      }

      hasBranches(data) {
        return data && data.status === 'success' && Array.isArray(data.branches) && data.branches.length > 0;
      }

      renderMessage(message, isLoading) {
        if (!this.body) return;
        this.body.replaceChildren();
        const notice = document.createElement('p');
        notice.classList.add('branch-availability-modal__message');
        if (isLoading) notice.classList.add('branch-availability__loading');
        notice.textContent = message;
        this.body.appendChild(notice);
      }

      statusIcon(isAvailable) {
        const template = this.root.querySelector(isAvailable ? '[data-icon-available]' : '[data-icon-not-available]');
        const icon = document.createElement('span');
        icon.classList.add(
          'branch-availability-branch__icon',
          isAvailable ? 'branch-availability-branch__icon--available' : 'branch-availability-branch__icon--not-available'
        );
        template && icon.appendChild(template.content.cloneNode(true));
        const status = document.createElement('span');
        status.classList.add('visually-hidden');
        status.textContent = isAvailable ? this.text.available : this.text.notAvailable;
        icon.appendChild(status);
        return icon;
      }

      caretIcon() {
        const caret = document.createElement('span');
        caret.classList.add('branch-availability-branch__caret');
        const template = this.root.querySelector('[data-icon-caret]');
        template && caret.appendChild(template.content.cloneNode(true));
        return caret;
      }

      branchInfo(branch) {
        const info = document.createElement('div');
        info.classList.add('branch-availability-branch__info');
        info.hidden = true;
        for (const value of [branch.address, branch.phone, branch.working_hours, branch.general_message]) {
          if (!value) continue;
          const row = document.createElement('p');
          row.textContent = value;
          info.appendChild(row);
        }
        return info;
      }

      branchElement(branch) {
        const isAvailable = Number(branch.inventory_status) > 0;
        const item = document.createElement('div');
        item.classList.add('branch-availability-branch');
        item.classList.toggle('branch-availability-branch--available', isAvailable);
        const info = this.branchInfo(branch);
        const hasInfo = info.childElementCount > 0;
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.classList.add('branch-availability-branch__toggle');
        toggle.appendChild(this.statusIcon(isAvailable));
        const name = document.createElement('span');
        name.classList.add('branch-availability-branch__name');
        name.textContent = branch.name;
        toggle.appendChild(name);
        if (hasInfo) {
          toggle.appendChild(this.caretIcon());
          toggle.setAttribute('aria-expanded', 'false');
          toggle.addEventListener('click', () => {
            const expanded = toggle.getAttribute('aria-expanded') === 'true';
            toggle.setAttribute('aria-expanded', String(!expanded));
            info.hidden = expanded;
          });
        } else {
          toggle.disabled = true;
        }
        item.appendChild(toggle);
        hasInfo && item.appendChild(info);
        return item;
      }

      renderBranches(branches) {
        if (!this.body) return;
        const list = document.createElement('div');
        list.classList.add('branch-availability-list');
        const fragment = document.createDocumentFragment();
        for (const branch of branches) fragment.appendChild(this.branchElement(branch));
        list.appendChild(fragment);
        this.body.replaceChildren(list);
      }
    }
  );
}